import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, ArrowRight, Check, Plus } from 'lucide-react';
import { PostJobModal } from './PostJobModal';
import { useAuth } from '../hooks/useAuth';
import './SplitHeroSection.css';

// Price-per-household ladder: shows the group discount dropping as more join.
const PRICE_LADDER = [
  { joined: '1 joined', price: '$180', pct: 100 },
  { joined: '3 joined', price: '$140', pct: 78 },
  { joined: '6 joined', price: '$110', pct: 61 },
  { joined: '10 joined', price: '$85', pct: 47, best: true },
];

const CUSTOMER_POINTS = [
  'Matched automatically with neighbors needing the same service',
  'Providers lower their price in steps as the group grows',
  'One job, one price. Everybody pays the group rate',
];

const PROVIDER_POINTS = [
  'Quality leads matched to your trade and service area',
  'Competitive bidding, no cold calls or chasing',
  'Manage projects, scheduling and payments in one place',
  'Build your reputation with verified customer reviews',
  'Group jobs on the same street, booked in one trip',
];

export const SplitHeroSection = ({ content, isModalOpen: isModalOpenProp, onOpenModal, onCloseModal }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isModalOpenLocal, setIsModalOpenLocal] = useState(false);

  const isModalOpen = isModalOpenProp !== undefined ? isModalOpenProp : isModalOpenLocal;
  const closeModal = () => onCloseModal ? onCloseModal() : setIsModalOpenLocal(false);

  const isPrelaunchMode = process.env.REACT_APP_PRELAUNCH_MODE === 'true';

  const go = (url) => () => navigate(url);

  // Editable text + CTAs come from the `split_hero` content section (admin-editable);
  // fall back to the built-in copy. The price ladder stays hard-coded (illustrative).
  const dc = {
    kicker: 'Group pricing just launched',
    headline: 'Your partner for home & business solutions',
    subtitle: 'Book trusted local pros, or team up with neighbors and pay group rates.',
    customer: {
      eyebrow: 'Group Buy Now · New', audience: 'For homeowners',
      title: 'Team up with neighbors, pay less',
      lede: 'Join people near you who need the same job done. The bigger the group gets, the more the provider knocks off the price, for everyone.',
      points: CUSTOMER_POINTS,
      cta_primary: { text: 'Join as a customer', url: '/signup?role=customer' },
      cta_secondary: { text: 'Find a group near you', url: '/groups' },
    },
    provider: {
      eyebrow: 'Service professionals', audience: 'For providers',
      title: 'Win more jobs, faster',
      lede: 'Quality leads, transparent bidding, and the tools to run your business day to day, plus group jobs that mean more work out of every trip.',
      points: PROVIDER_POINTS,
      cta_primary: { text: 'Join as a provider', url: '/signup?role=provider' },
      cta_secondary: { text: 'See how bidding works', url: '/group-buy' },
    },
  };
  const c = content || {};
  const cust = { ...dc.customer, ...(c.customer || {}) };
  const prov = { ...dc.provider, ...(c.provider || {}) };
  const custCtaP = { ...dc.customer.cta_primary, ...(c.customer?.cta_primary || {}) };
  const custCtaS = { ...dc.customer.cta_secondary, ...(c.customer?.cta_secondary || {}) };
  const provCtaP = { ...dc.provider.cta_primary, ...(c.provider?.cta_primary || {}) };
  const provCtaS = { ...dc.provider.cta_secondary, ...(c.provider?.cta_secondary || {}) };
  const custPoints = (c.customer?.points?.length ? c.customer.points : dc.customer.points);
  const provPoints = (c.provider?.points?.length ? c.provider.points : dc.provider.points);

  return (
    <>
      <section className="fd-hero">
        {/* Centered header */}
        <div className="fd-hero__intro">
          <span className="fd-hero__kicker">
            <Plus size={13} className="fd-hero__kicker-plus" strokeWidth={3} />
            {c.kicker || dc.kicker}
          </span>
          <h1 className="fd-hero__headline">{c.headline || dc.headline}</h1>
          <p className="fd-hero__sub">{c.subtitle || dc.subtitle}</p>
        </div>

        <div className="fd-hero__split">
          {/* Customer column (blue accent) */}
          <div className="fd-hero__col fd-hero__col--customer">
            <div className="fd-hero__label">
              <span className="fd-hero__badge fd-hero__badge--customer"><Users size={18} /></span>
              <span className="fd-hero__label-text">
                <span className="fd-hero__eyebrow fd-hero__eyebrow--customer">{cust.eyebrow}</span>
                <span className="fd-hero__audience">{cust.audience}</span>
              </span>
            </div>

            <h2 className="fd-hero__title">{cust.title}</h2>
            <p className="fd-hero__lede">{cust.lede}</p>

            {/* Price-per-household demo card */}
            <div className="fd-price">
              <div className="fd-price__head">
                <span>Price per household</span>
                <span className="fd-price__service">Roof gutter cleaning</span>
              </div>
              <ul className="fd-price__rows">
                {PRICE_LADDER.map((r) => (
                  <li key={r.joined} className={`fd-price__row ${r.best ? 'fd-price__row--best' : ''}`}>
                    <span className="fd-price__joined">{r.joined}</span>
                    <span className="fd-price__bar">
                      <span className="fd-price__fill" style={{ width: `${r.pct}%` }} />
                    </span>
                    <span className="fd-price__amt">{r.price}</span>
                  </li>
                ))}
              </ul>
              <p className="fd-price__note">
                Your street is at <strong>6 neighbors</strong>. 4 more unlocks the next price drop.
              </p>
              <p className="fd-price__disclaimer">Example only. Actual prices vary by provider and job.</p>
            </div>

            <ul className="fd-hero__points fd-hero__points--customer">
              {custPoints.map((p) => (
                <li key={p}><Check size={16} />{p}</li>
              ))}
            </ul>

            <div className="fd-hero__ctas">
              <button className="fd-btn fd-btn--customer" onClick={go(custCtaP.url)}>
                {custCtaP.text} <ArrowRight size={16} />
              </button>
              <button className="fd-btn fd-btn--ghost" onClick={go(custCtaS.url)}>
                {custCtaS.text}
              </button>
            </div>
          </div>

          {/* Provider column (primary/emerald accent) */}
          <div className="fd-hero__col fd-hero__col--provider">
            <div className="fd-hero__label">
              <span className="fd-hero__badge fd-hero__badge--provider"><Briefcase size={18} /></span>
              <span className="fd-hero__label-text">
                <span className="fd-hero__eyebrow fd-hero__eyebrow--provider">{prov.eyebrow}</span>
                <span className="fd-hero__audience">{prov.audience}</span>
              </span>
            </div>

            <h2 className="fd-hero__title">{prov.title}</h2>
            <p className="fd-hero__lede">{prov.lede}</p>

            <ul className="fd-hero__points fd-hero__points--provider">
              {provPoints.map((p) => (
                <li key={p}><Check size={16} />{p}</li>
              ))}
            </ul>

            <div className="fd-hero__ctas">
              <button className="fd-btn fd-btn--provider" onClick={go(provCtaP.url)}>
                {provCtaP.text} <ArrowRight size={16} />
              </button>
              <button className="fd-btn fd-btn--ghost" onClick={go(provCtaS.url)}>
                {provCtaS.text}
              </button>
            </div>
          </div>
        </div>
      </section>

      <PostJobModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};
